



































































import _ from 'lodash'
import Component, {mixins} from 'vue-class-component'
import CountrySelector from '@/modules/geo/components/CountrySelector.vue'
import AdminDivisionSelector from '@/modules/geo/components/AdminDivisionSelector.vue'
import {Country, AdminDivision} from '@/modules/geo/types'
import {State, Mutation} from 'vuex-class';
import Rate from '@/modules/shipping/model/Rate'
import Region from '@/modules/shipping/model/Region'
import RegionDetailMode from '@/modules/shipping/model/RegionDetailMode'
import RegionTypes from '@/modules/shipping/model/RegionTypes'
import StyleMixin from '@/modules/core/style/StyleMixin';
import { notify, Colors } from '@/modules/core/notification'

    /**
     * Detalle de región
     *
     * Muestra una pantalla para registrar una nueva región de envío a una tarifa
     * o editar una existente
     *
     * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
     */
@Component({
        components: {
            CountrySelector,
            AdminDivisionSelector,
        },
    })
    export default class RegionDetail extends mixins(StyleMixin) {
        /**
         * Tarifa actualmente mostrada al usuario y en la cual se encuentra declarada
         * la región que esta siendo editada
         */
        @State(state => state.shipping.currentRate)
        public rate: Rate

        /**
         * Región actualmente en edición o creación
         */
        @State(state => state.shipping.currentRegion)
        public region: Region

        /**
         * Modo de edición actual edición, creación o ninguno
         */
        @State(state => state.shipping.regionDetailMode)
        public mode: RegionDetailMode

        /**
        *
        */
        @State (state => state.shipping.regions)
        public regions: Region[]

        /**
         * Mutación para modificar la región actual
         */
        @Mutation('shipping/setCurrentRegion')
        public setRegion: (region: Region) => void

        /**
         * Mutación para cambiar el modo de edición
         */
        @Mutation('shipping/setRegionDetailMode')
        public setMode: (mode: RegionDetailMode) => void

        @Mutation('shipping/region/add')
        public addRegion: (region: Region) => void

        @Mutation('shipping/region/update')
        public updateRegionOnList: (region: Region) => void

        private currentCardName = ''

        /**
         * Comprueba si la región es una zona de divisiones administrativas
         */
        get isZoneOfAdminDivisions (): boolean {
            return this.isZone && this.regionType === RegionTypes.AdminDivision
        }

        /**
         * Nombre de la pantalla
         */
        get cardName (): string {
            if (this.mode === RegionDetailMode.None) {
                return this.currentCardName
            }

            const name = this.mode === RegionDetailMode.Editing ? 'Modificar región' : 'Crear región'
            this.currentCardName = name

            return name
        }

        get saveButtonName (): string {
            return this.mode === RegionDetailMode.Editing ? 'Editar' : 'Añadir'
        }

        /**
         * Tipos de regiones disponibles, para el radio button de selección de
         * tipo
         */
        public regionTypes = [
            {type: RegionTypes.Country, name: 'Pais', whenZone: 'Zona de países'},
            {
                type: RegionTypes.AdminDivision,
                name: 'División Administrativa',
                whenZone: 'Zona de div. administrativas',
            },
        ]

        public currentCountry = new Country('', '')

        /**
         * País seleccionado
         */
        get selectedCountry (): Country | Country[] {
            if (this.isZoneOfAdminDivisions) {
                return this.region.geodata as Country
            }

            if (this.isCountry) {
                // Si no es una zona se retorna la entidad geografica de la region
                if (!this.isZone) {
                    return this.region.geodata as Country
                }

                return this.region.children.map(r => r.geodata as Country)
            }

            return this.currentCountry || new Country('', '')
        }

        /**
         * Actualiza un campo de la región actualmente en edición
         */
        public update (field: string, value: any) {
            this.$store.commit('shipping/UPDATE_REGION_FIELD', {field, value})
        }

        /**
         * Actualiza los paises de la región
         */
        public updateCountry (country: Country | Country[]) {
            if (this.isAdminDivision) {
                this.currentCountry = country as Country
                // Si la región es una zona de divisiones de administrativas el país se
                // guarda en la propiedad geodata. Esto permite saber de forma sencilla
                // de que pais son las divisiones seleccionadas
                this.isZoneOfAdminDivisions && this.update('geodata', country)

                return
            }

            // Si la región no es una zona se actualiza directamente la entidad geográfica
            // de la región
            if (!this.isZone) {
                this.update('geodata', country)

                return
            }

            const items = Array.isArray(country) ? country : [country]
            this.update('children', items.map(c => Region.ofCountry(c)))
        }

        /**
         * Método llamado por el componente AdminDivisionSelector cuando el usuario selecciona
         * divisiones administrativas.
         */
        public updateDivisions (divisions: AdminDivision | AdminDivision[]) {
            // La región actual no es una zona de divisiones administrativas
            if (!this.region.isAdminDivisionZone) {
                this.update('geodata', divisions)

                return
            }

            const children = (divisions as AdminDivision[]).map(d => Region.ofAdminDivision(d))
            this.update('children', children)
            this.$forceUpdate()
        }

        /**
         * Obtiene las divisiones seleccionadas por el usuario
         */
        public get selectedAdminDivisions () {
            return this.region.children.map(r => r.geodata)
        }

        get isCountry (): boolean {
            return this.region.type === RegionTypes.Country
        }

        get isAdminDivision (): boolean {
            return this.region.type === RegionTypes.AdminDivision
        }

        get isZone (): boolean {
            return this.region.isZone
        }

        set isZone (isZone: boolean) {
            this.update('isZone', isZone)
            if (isZone && this.isAdminDivision) {
                this.update('geodata', this.currentCountry)
                this.update('children', [])
            }
        }

        get isExcluded (): boolean {
            return this.region.isExcluded
        }

        set isExcluded (excluded: boolean) {
            this.update('isExcluded', true)
        }

        get regionType (): RegionTypes {
            return this.region.type
        }

        /**
         * Establece el tipo de región
         */
        set regionType (type: RegionTypes) {
            this.update('type', type)
        }

        public get freeShippingFrom (): any {
            return this.region.freeShippingFrom
        }

        public set freeShippingFrom (value: any) {
            let number = parseFloat(value)
            if (isNaN(number)) {
                number = 0
            }

            this.update('freeShippingFrom', number)
        }

        get regionName (): string {
            if (this.region.isZone) {
                return this.region.name
            }

            const geodata = this.region.geodata
            if (geodata) {
                return geodata.name
            }

            return ''
        }

        /**
         * Confirmar cambios de los detalles
         * de la región.
         * @author José M. Mármol <jmarmol@softcontrol.es>
         */
        public confirm () {
            const tempId = _.uniqueId('region_')
            let exists = false

            if (this.region.tempId === '' && (this.mode === RegionDetailMode.Editing || this.mode === RegionDetailMode.Creating)) {
              this.region.tempId = tempId
            }
            if (this.rate && this.rate.id !== 0) {
                this.region.rate.id = this.rate.id
            }
            const copy = _.clone(this.region)

            // Añadir a la lista de regiones si esta activo el modo de creación
            if (this.mode === RegionDetailMode.Creating) {
                this.regions.forEach((region: Region) => {
                    if (copy.isCountry) {
                        if (copy.regionId === region.regionId && copy.tempId !== region.tempId && copy.type === region.type) {
                            notify('El país ya existe en la tarifa', Colors.Error)
                            exists = true
                            return
                        }
                    }
                    if (copy.isAdminDivisionZone && this.mode === RegionDetailMode.Creating) {
                        if (copy.regionId === region.regionId && copy.tempId !== region.tempId && copy.type === region.type) {
                            return
                        }
                    }
                })
                if (!exists) {
                    this.addRegion(copy)
                }

            }
            this.region.tempId = ''

            // Actualizar la región en la lista de regiones del estado si el modo
            // es de edición
            if (this.mode === RegionDetailMode.Editing) {
                this.updateRegionOnList(copy)
            }

            this.setMode(RegionDetailMode.None)
        }

        public cancel () {
            this.setMode(RegionDetailMode.None)
        }
    }
